import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { ROLE_LABELS, STAFF_ROLES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, FileText, Wrench, ScrollText, UserCog,
  Moon, Sun, LogOut, Gauge, ShieldCheck,
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", roles: null },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers-link", roles: STAFF_ROLES },
  { to: "/quotations", label: "Quotations", icon: FileText, testid: "nav-quotations-link", roles: null },
  { to: "/equipment", label: "Equipment", icon: Wrench, testid: "nav-equipment-link", roles: null },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit-link", roles: ["admin", "quality_manager"] },
  { to: "/users", label: "Users", icon: UserCog, testid: "nav-users-link", roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const items = NAV_ITEMS.filter((i) => !i.roles || i.roles.includes(user.role));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-3 px-4 sm:px-6">
          <NavLink to="/" className="flex items-center gap-2" data-testid="nav-brand-link">
            <Gauge className="h-6 w-6 text-primary" />
            <span className="font-display text-lg font-bold tracking-tight">BMDLabsystem</span>
            <span className="hidden rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3 text-emerald-500" /> ISO/IEC 17025 Accreditated
            </span>
          </NavLink>
          <nav className="ml-4 hidden items-center gap-1 md:flex" data-testid="main-nav">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-button" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-button"
                  className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm transition-colors hover:bg-accent"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-sm bg-primary font-mono text-[11px] font-semibold text-primary-foreground">
                    {user.name?.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="hidden max-w-[140px] truncate sm:inline">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-primary">
                    {ROLE_LABELS[user.role] || user.role}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-border px-4 py-1.5 md:hidden" data-testid="main-nav-mobile">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon className="h-3.5 w-3.5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
